import React, { useState, useEffect } from "react";
import "./BannerManagement.scss";
import { FiEdit, FiTrash2, FiPlus, FiImage } from "react-icons/fi";
import { bannerApi } from "../../config/api";
import { apiFormDataClient, apiClient } from "../../utils/apiClient";
import { compressImage } from "../../utils/imageCompressor";
import { getImageUrl, handleImageError } from "../../utils/imageUrl";

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentBanner, setCurrentBanner] = useState({
    _id: "",
    Banner_Image: "",
    Banner_Title: "",
    Banner_Location: "",
    Status: true,
    Sort_Order: 0
  });

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${bannerApi}`);
      const result = await response.json();
      if (result.data) {
        setBanners(result.data);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      alert("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentBanner(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'Sort_Order' ? Number(value) : value)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const compressed = await compressImage(file);
    setSelectedFile(compressed);
    const reader = new FileReader();
    reader.readAsDataURL(compressed);
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = modalMode === "add" ? `${bannerApi}` : `${bannerApi}/${currentBanner._id}`;
      const method = modalMode === "add" ? "POST" : "PUT";
      
      let imageString = currentBanner.Banner_Image || "";
      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        imageString = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(compressed);
          reader.onloadend = () => resolve(reader.result);
        });
      }

      if (!imageString) {
        alert("Please select a banner image.");
        return;
      }

      const payload = {
        Banner_Image: imageString,
        Banner_Title: currentBanner.Banner_Title || "",
        Banner_Location: currentBanner.Banner_Location || "",
        Status: currentBanner.Status !== undefined ? currentBanner.Status : true,
        Sort_Order: Number(currentBanner.Sort_Order || 0),
      };

      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        fetchBanners();
        setShowModal(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Backend error:", errorData);
        alert(`Failed to save banner: ${errorData.message || response.statusText || ""} ${errorData.error ? "| " + errorData.error : ""}`);
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      alert(`Failed to save banner: ${error.message || "Network error"}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    
    try {
      const response = await apiClient(`${bannerApi}/${id}`, { method: "DELETE" });
      if (response.ok) {
        fetchBanners();
      } else {
        alert("Failed to delete banner");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      alert("Failed to delete banner");
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setCurrentBanner({ _id: "", Banner_Image: "", Banner_Title: "", Banner_Location: "", Status: true, Sort_Order: 0 });
    setSelectedFile(null);
    setPreviewUrl(null);
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setModalMode("edit");
    setCurrentBanner(banner);
    setSelectedFile(null);
    setPreviewUrl(banner.Banner_Image);
    setShowModal(true);
  };

  return (
    <div className="banner-management-page">
      <div className="page-header">
        <h2>Hero Banner Management</h2>
        <button className="add-btn" onClick={openAddModal}>
          <FiPlus /> Add New Banner
        </button>
      </div>

      <div className="banner-list">
        {loading ? (
          <p>Loading banners...</p>
        ) : banners.length === 0 ? (
          <p>No banners found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Location</th>
                <th>Status</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((banner) => (
                <tr key={banner._id}>
                  <td>
                    <img src={getImageUrl(banner.Banner_Image, bannerApi)} onError={(e) => handleImageError(e, "Banner")} alt="Banner" width="100" />
                  </td>
                  <td>{banner.Banner_Title || "-"}</td>
                  <td>{banner.Banner_Location || "-"}</td>
                  <td>{banner.Status ? "Active" : "Inactive"}</td>
                  <td>{banner.Sort_Order}</td>
                  <td>
                    <button className="icon-btn edit" onClick={() => openEditModal(banner)}><FiEdit /></button>
                    <button className="icon-btn delete" onClick={() => handleDelete(banner._id)}><FiTrash2 /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{modalMode === "add" ? "Add New Banner" : "Edit Banner"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Banner Image</label>
                <div className="image-upload-wrapper">
                  {previewUrl || currentBanner.Banner_Image ? (
                    <img src={previewUrl || getImageUrl(currentBanner.Banner_Image, bannerApi)} onError={(e) => handleImageError(e, "Preview")} alt="Preview" className="img-preview" />
                  ) : (
                    <div className="placeholder"><FiImage size={40} /></div>
                  )}
                  <input type="file" accept="image/*" onChange={handleImageUpload} required={modalMode === 'add' && !currentBanner.Banner_Image} />
                </div>
              </div>
              <div className="form-group">
                <label>Title (Optional)</label>
                <input type="text" name="Banner_Title" value={currentBanner.Banner_Title} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Location text (Optional)</label>
                <input type="text" name="Banner_Location" value={currentBanner.Banner_Location} onChange={handleInputChange} />
              </div>
              <div className="form-group row">
                <label>Sort Order</label>
                <input type="number" name="Sort_Order" value={currentBanner.Sort_Order} onChange={handleInputChange} />
              </div>
              <div className="form-group row">
                <label>Active Status</label>
                <input type="checkbox" name="Status" checked={currentBanner.Status} onChange={handleInputChange} />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="save-btn">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerManagement;